import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBriefcase,
  faCube,
  faFile,
  faSparkles,
  faTable,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";

import { Select } from "src/base-components/Select";
import { CaseBlockConfigOutput } from "src/clients/case-management";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import {
  useWorkspaceFeatureGates,
  WorkspaceFeatureGates,
} from "src/hooks/useWorkspaceFeatureGates";
import { FEATURE_FLAGS } from "src/router/featureFlags";

type BlockKind = {
  icon?: IconProp;
  key: CaseBlockConfigOutput["config"]["kind"];
  value: string;
  description: string;
};

const BASE_BLOCKS_KINDS: BlockKind[] = [
  {
    icon: faTable,
    key: "table",
    value: "Table",
    description: "Shows structured data in a tabular layout",
  },
  {
    icon: faSparkles,
    key: "markdown",
    value: "Markdown",
    description:
      "Displays custom text with simple formatting such as headings, lists, bold text, and links",
  },
  {
    icon: faBriefcase,
    key: "case_details",
    value: "Case Details",
    description: "Shows case properties in a tabular format",
  },
] as const;

const FILES_VIEWER_BLOCK_KIND: BlockKind = {
  icon: faFile,
  key: "files_viewer",
  value: "Files Viewer",
  description: "Displays a file attached to the case",
} as const;

const ENTITIES_BLOCKS_KINDS: BlockKind[] = [
  {
    icon: faCube,
    key: "entity_details",
    value: "Entity Viewer",
    description:
      "Displays the latest properties of a specified Entity in a table",
  },
  {
    icon: faCube,
    key: "related_entities",
    value: "Related Entities",
    description: "Shows all entities related to the selected Entity",
  },
  {
    icon: faBriefcase,
    key: "related_cases",
    value: "Related Cases",
    description: "Shows all cases related to the selected Entity",
  },
] as const;

const FEATURES_EVENTS_BLOCKS_KINDS: BlockKind[] = [
  {
    icon: faTimer,
    key: "event_details",
    value: "Event Viewer",
    description: "Displays the properties of a specified event in a table",
  },
  {
    icon: faTimer,
    key: "related_events",
    value: "Related Events",
    description: "Shows all events related to the selected Entity",
  },
] as const;

export const BlockKindSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  dataLoc?: string;
}> = ({ value, onChange, disabled, dataLoc }) => {
  const featureGates = useWorkspaceFeatureGates();
  const isFilesViewerEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.filesViewerBlock,
  );

  return (
    <Select
      dataLoc={dataLoc}
      disabled={disabled}
      options={getOptions(featureGates, isFilesViewerEnabled)}
      value={value}
      onChange={onChange}
    />
  );
};

const getOptions = (
  featureGates: WorkspaceFeatureGates,
  isFilesViewerEnabled: boolean,
) => {
  const baseKinds = isFilesViewerEnabled
    ? [...BASE_BLOCKS_KINDS, FILES_VIEWER_BLOCK_KIND]
    : BASE_BLOCKS_KINDS;

  if (featureGates.featuresEventsEnabled) {
    return [
      ...baseKinds,
      ...ENTITIES_BLOCKS_KINDS,
      ...FEATURES_EVENTS_BLOCKS_KINDS,
    ];
  }
  if (featureGates.entitiesEnabled) {
    return [...baseKinds, ...ENTITIES_BLOCKS_KINDS];
  }
  return baseKinds;
};
